import { IMessage } from '@/Core.Service.Domain/Strategies/Strategies/types/replayStrategy.ts';

// POST /v1/strategy/what-if answers 200 for every rule that ran, and an empty
// list means "the rule ran and no condition matched" -- a successful outcome,
// not an error. Left alone that renders as an empty overlay, which reads as a
// broken screen, so it gets a message of its own.
export const NO_ACTION_TAKEN_MESSAGE =
  'No action taken -- likely no condition matched.';

/**
 * Turns a what-if response into the messages the stepper renders.
 *
 * Anything that is not a non-empty array counts as "no action taken": the
 * envelope's `data` is absent or null on a 200 whenever the handler produced
 * nothing, and `[].length` on that would throw and surface as an error the run
 * never had.
 */
export const toWhatIfMessages = (
  response: IMessage[] | null | undefined
): IMessage[] =>
  Array.isArray(response) && response.length > 0
    ? response
    : [{ message: NO_ACTION_TAKEN_MESSAGE, messageType: null }];

/**
 * The counterpart for a request that never reached a verdict. `messageType`
 * matches what the backend flags a rule exception with, so the stepper marks it
 * red the same way.
 */
export const toErrorMessages = (message: string): IMessage[] => [
  { message, messageType: 'Error' },
];

// The axios interceptor rejects with the response body, so a failed what-if
// arrives here as the API's error envelope rather than an Error.
interface IApiError {
  error?: string | null;
  errors?: Record<string, string[]> | null;
}

// A rule that fails validation comes back with CodeEffects' client payload in
// `error`: not JSON (the keys are unquoted), just a blob of `c:` element ids and
// `h:` hints. The ids name divs in the rule editor and mean nothing to a reader,
// so only the hints are worth showing, and the same hint repeats once per
// offending element.
const CODE_EFFECTS_HINT = /h:"((?:[^"\\]|\\.)*)"/g;

const toCodeEffectsHints = (error: string): string[] =>
  [
    ...new Set(
      [...error.matchAll(CODE_EFFECTS_HINT)].map((match) =>
        match[1].replace(/\\(.)/g, '$1').trim()
      )
    ),
  ].filter(Boolean);

/**
 * Turns whatever the API rejected with into messages the stepper renders,
 * falling back to `fallback` when there is nothing quotable in it -- a network
 * failure rejects with an Error, which carries nothing worth showing a user.
 */
export const toApiErrorMessages = (
  error: IApiError | string | null | undefined,
  fallback: string
): IMessage[] => {
  if (typeof error === 'string' && error.trim()) {
    return toErrorMessages(error);
  }

  if (error && typeof error === 'object') {
    // ASP.NET's validation dictionary: { field: ["message", ...] }.
    const fieldMessages = Object.values(error.errors ?? {})
      .flat()
      .filter(Boolean);
    if (fieldMessages.length > 0) {
      return fieldMessages.flatMap((message) => toErrorMessages(message));
    }

    if (typeof error.error === 'string' && error.error.trim()) {
      const hints = toCodeEffectsHints(error.error);
      return hints.length > 0
        ? hints.flatMap((hint) => toErrorMessages(hint))
        : toErrorMessages(error.error);
    }
  }

  return toErrorMessages(fallback);
};
